<template>
  <div class="settings-block">
    <div class="settings-block__title">
      <div class="settings-block__title-text">
        {{ t('Redirect') }}
      </div>
      <div class="settings-block__title-buttons">
        <ui-button
          v-show="!edit"
          :text="t('Edit')"
          icon="fas fa-edit"
          button-style="outline-accent"
          @click="setEdit(true)"
        />
        <ui-button
          v-show="edit"
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="putLoading"
          @click="setEdit(false)"
        />
        <ui-button
          v-show="edit"
          :text="t('Save')"
          icon="fas fa-check"
          button-style="accent"
          :loading="putLoading"
          @click="submit"
        />
      </div>
    </div>
    <div class="settings-block__content">
      <div class="flex-wrap__checkbox">
        <ui-checkbox
          v-model="form.trackConversionsNoClickId"
          :label="t('Track Conversions Without Click ID')"
          :disabled="!edit"
          :help-tooltip="
            t(
              'Choose this option to track conversions without click IDs, particularly useful when working with the platforms like Appsflyer that cannot transmit detailed conversion data. <br>Conversions are still assigned to specific campaigns through the <b>attribution=cid-caid</b> parameter, even in the absence of click IDs.<br>If additional parameters such as <b>oid, lpid, flid, eid, or custom 1-10</b> are unnecessary, you can remove them from the postback URL or pixel.',
            )
          "
        />
      </div>
      <div class="flex-wrap__checkbox">
        <ui-label>
          {{ t('Block archived campaigns') }}
        </ui-label>
        <ui-checkbox
          v-model="form.stopArchivedCampaigns"
          :label="t('From receiving traffic')"
          :disabled="!edit"
        />
      </div>

      <div class="flex-wrap__checkbox">
        <ui-label>
          {{ t('Manage Suspicious Cost') }}
        </ui-label>
        <ui-checkbox
          v-model="form.allowSuspiciousReferrer"
          :label="t('Allow google.com Referrer')"
          :disabled="!edit"
          :help-tooltip="
            t(
              'By default the option of tracking visits from http://google.com and costs more than $1 is switched on.<br>If you want to exclude this traffic, unmark the checkbox.',
            )
          "
        />
      </div>

      <div class="flex-wrap__checkbox">
        <ui-checkbox
          v-model="form.allowBotUserAgents"
          :label="t('Allow Bot User-Agents')"
          :disabled="!edit"
          :help-tooltip="
            t(
              'By default, the tracker rejects traffic from User-Agents commonly associated with bots. Enable this checkbox to allow receiving such traffic.',
            )
          "
        />
      </div>

      <div class="flex-wrap__checkbox settings-form__item">
        <ui-input
          v-model="form.trafficErrorReturnUrl"
          name="trafficErrorReturnUrl"
          :placeholder="t('e.g. http://www.example.com/')"
          :label="t('Forward unknown clicks to URL')"
          :label-help="
            t(
              'All the clicks for which campaign can not be identified, will be forwarded to this URL. The reason might be incorrect URL or deleted cookies.',
            )
          "
          :disabled="!edit || putLoading"
          :error-messages="fieldErrors['trafficErrorReturnUrl']"
        />
      </div>
      <div class="flex-wrap__checkbox">
        <ui-label>
          {{ t('Tracking OS, Browser and Device Data') }}
        </ui-label>
        <ui-checkbox
          v-model="form.userAgentClientHintsEnabled"
          :label="t('Use Client Hints')"
          :disabled="!edit"
          :help-tooltip="
            t(
              'Select this option if you wish to request the complete details on OS, browser and device data.<br><br>Note that with the activation of this option the additional redirect will happen which will slow the redirect in your campaigns.',
            )
          "
        />
      </div>
      <ui-info class="mb-16" width="width-fit-content">
        <span
          v-html="
            t(
              'When using this option, it is required to use HTTPS instead of HTTP in all the campaign links.<br>To accurately track OS, device, browser data in previously created campaigns, go to the campaign settings on the side of traffic source and add S to HTTP in the link.<br>The links of offers and landing pages in the campaign should also contain HTTPS.',
            )
          "
        />
      </ui-info>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from 'vue';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import parseApiErrors from '@/utils/parseApiErrors';
import type { FieldErrors } from '@/services/models';
import { putUserApiRequest } from '@/services/api/user-api';
import type { User } from '@/services/models';

const { t } = useI18n();
const store = useStore();

const edit = ref(false);
const putLoading = ref(false);
const fieldErrors = ref<FieldErrors>({});
type RedirectForm = Pick<
  User,
  | 'trafficErrorReturnUrl'
  | 'allowSuspiciousReferrer'
  | 'userAgentClientHintsEnabled'
  | 'trackConversionsNoClickId'
  | 'stopArchivedCampaigns'
  | 'allowBotUserAgents'
>;

const form = reactive<RedirectForm>({
  trafficErrorReturnUrl: '',
  allowSuspiciousReferrer: '',
  userAgentClientHintsEnabled: false,
  trackConversionsNoClickId: false,
  stopArchivedCampaigns: null,
  allowBotUserAgents: false,
});

const getUserData = (user: User): RedirectForm => {
  return {
    trafficErrorReturnUrl: user.trafficErrorReturnUrl ?? '',
    allowSuspiciousReferrer: user.allowSuspiciousReferrer ?? '',
    userAgentClientHintsEnabled: user.userAgentClientHintsEnabled || false,
    trackConversionsNoClickId: user.trackConversionsNoClickId || false,
    stopArchivedCampaigns:
      user.stopArchivedCampaigns === 1 ? true : user.stopArchivedCampaigns,
    allowBotUserAgents: user.allowBotUserAgents || false,
  };
};

const clearFieldErrors = () => {
  fieldErrors.value = {};
};

const setEdit = (value: boolean) => {
  edit.value = value;
  if (!value) {
    Object.assign(form, getUserData(store.user as User));
    clearFieldErrors();
  }
};

const submit = async () => {
  clearFieldErrors();
  putLoading.value = true;
  try {
    const res = await putUserApiRequest(form);
    store.$patch(state => {
      state.user = { ...state.user, ...res };
    });
    edit.value = false;
    $emitter.emit('toast-success', t('Changes have been successfully saved'));
  } catch (error: unknown) {
    fieldErrors.value = parseApiErrors({ error, nested: true });
  } finally {
    putLoading.value = false;
  }
};

watch(
  () => store.user,
  () => {
    if (!edit.value) {
      Object.assign(form, getUserData(store.user as User));
    }
  },
  { deep: true },
);

onMounted(() => {
  Object.assign(form, getUserData(store.user as User));
});
</script>

<style lang="scss" scoped>

.redirect-form {
  &__return-url-tooltip {
    max-width: 700px !important;
    word-wrap: break-word;
  }
}
</style>
